import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import CircularProgress from '@mui/material/CircularProgress'

import StyledModal from '../../components/StyledModal/StyledModal'
import StyledTextFieldWithLabel from '../../components/StyledTextFieldWithLabel/StyledTextFieldWithLabel'
import { showNotification } from '../../utils/toast.utils'

import idleTimeReasonService from '../../service/ideTimeReason.service'

const Table = styled('table')(() => ({
  borderCollapse: 'collapse',
  width: '100%'
}))

const TableRow = styled('tr')(() => ({}))

const TableHead = styled('th')(() => ({
  textAlign: 'left',
  padding: '8px',
  border: '1px solid #bbbbbb',
  backgroundColor: '#dfdfdf'
}))

const TableCell = styled('td')(() => ({
  border: '1px solid #dddddd',
  textAlign: 'left',
  padding: '8px',
  '&:first-child': {
    borderBottomLeftRadius: '8px'
  },
  '&:last-child': {
    borderBottomRightRadius: '8px'
  }
}))

const Editable = ({ index, reason, color, onRemove, onUpdate }) => {
  const [isEditEnabled, setIsEditEnabled] = useState(false)
  const [inputValue, setInputValue] = useState(reason)
  const [selectedColor, setSelectedColor] = useState(color || '#000000')

  return (
    <div>
      {isEditEnabled ? (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            style={{
              width: '100%',
              padding: '4px',
              borderRadius: '4px',
              outline: 'none',
              border: '1px solid gray'
            }}
          />
          <input
            type="color"
            style={{ width: '40px', cursor: 'pointer' }}
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
          />
          <CheckIcon
            onClick={() => {
              onUpdate(index, inputValue, selectedColor)
              setIsEditEnabled(false)
            }}
            sx={{
              padding: '2px',
              cursor: 'pointer',
              border: '1px solid gray',
              borderRadius: '4px',
              '&:hover': { backgroundColor: '#dddddd' }
            }}
          />
          <ClearIcon
            sx={{
              padding: '2px',
              cursor: 'pointer',
              border: '1px solid gray',
              borderRadius: '4px',
              '&:hover': { backgroundColor: '#dddddd' }
            }}
            onClick={() => setIsEditEnabled(false)}
          />
        </div>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer'
          }}
        >
          <Stack
            sx={{ flexDirection: 'row', gap: '8px', alignItems: 'center' }}
          >
            <span style={{ color: color }}>{reason}</span>
            <div
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: color,
                borderRadius: '50%'
              }}
            ></div>
          </Stack>
          <div style={{ display: 'flex', gap: '8px' }}>
            <EditIcon
              onClick={() => setIsEditEnabled(true)}
              sx={{
                padding: '2px',
                cursor: 'pointer',
                borderRadius: '4px',
                '&:hover': { backgroundColor: '#dddddd' }
              }}
            />
            <DeleteOutlineOutlinedIcon
              onClick={() => onRemove(index)}
              sx={{
                padding: '2px',
                cursor: 'pointer',
                borderRadius: '4px',
                '&:hover': { backgroundColor: '#dddddd' }
              }}
            />
          </div>
        </Box>
      )}
    </div>
  )
}

const IdleTimeReasonModal = ({
  isModalOpen,
  closeHandler,
  onSaveSuccess,
  isLoading
}) => {
  const [reason, setReason] = useState('')
  const [color, setColor] = useState('#000000')
  const [reasons, setReasons] = useState([])
  const [loading, setLoading] = useState(false)

  const addReason = () => {
    if (!reason) return
    setReasons((prev) => [...prev, { idleReason: reason.trim(), color }])
    setReason('')
  }

  const removeReason = (index) => {
    setReasons((prev) => prev.filter((_, i) => i !== index))
  }

  const updateReason = (index, updatedReason, updatedColor) => {
    setReasons((prev) =>
      prev.map((item, i) =>
        i === index
          ? { idleReason: updatedReason.trim(), color: updatedColor }
          : item
      )
    )
  }

  const saveReasons = async () => {
    if (reasons.length === 0) {
      showNotification('Add at least one reason', 'error', 'TOP_CENTER')
      return
    }
    setLoading(true)
    try {
      const response = await idleTimeReasonService.addIdleTimeReasons(reasons)
      if (response.success) {
        showNotification('Idle time reasons added successfully', 'success')
        onSaveSuccess()
        closeHandler()
      } else {
        showNotification('Failed to add idle time reasons', 'error')
      }
    } catch (error) {
      console.error('Error:', error)
      showNotification('Failed to add idle time reasons', 'error')
    }
    setLoading(false)
  }

  return (
    <StyledModal
      modalWidth={500}
      loaderState={isLoading}
      isModalOpen={isModalOpen}
      modalTitle={'Add Idle Time Reasons'}
      modalCloseHandler={closeHandler}
    >
      <Stack spacing={2} sx={{ paddingTop: '16px', width: '100%' }}>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <StyledTextFieldWithLabel
            placeholderText="Enter reason, select color & click add"
            textValue={reason}
            onChangeHandler={(e) => setReason(e.target.value)}
            isDisabled={false}
          />
          <Stack
            sx={{
              width: '40px',
              height: '40px',
              alignSelf:'center',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #a8a8b5',
              borderRadius: '4px'
            }}
          >
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{
                width: '80%',
                height: '80%',
                border: 'none',
                cursor: 'pointer'
              }}
            />
          </Stack>
          <Button
            variant="outlined"
            onClick={addReason}
            disabled={!reason || !color}
          >
            Add
          </Button>
        </Box>
        <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
          {reasons.length > 0 && (
            <Table>
              <TableRow>
                <TableHead>S No</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
              {reasons.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Editable
                      index={index}
                      reason={item.idleReason}
                      color={item.color}
                      onRemove={removeReason}
                      onUpdate={updateReason}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          )}
        </Box>
      </Stack>
      <Stack
        spacing={2}
        direction="row"
        sx={{ marginTop: '16px', justifyContent: 'flex-end' }}
      >
        <Button variant="outlined" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ color: '#fff' }}
          onClick={saveReasons}
          disabled={loading || reasons.length === 0}
          startIcon={
            loading && <CircularProgress size={14} sx={{ color: '#fff' }} />
          }
        >
          Save
        </Button>
      </Stack>
    </StyledModal>
  )
}

export default IdleTimeReasonModal
